import { useState, useEffect } from 'react';
import { Send, Search, Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Modal, LimitReachedModal } from '../ui';
import { useTranslation } from '../../context/LanguageContext';
import {
    searchParticipantsAndGroups, findOrCreateDirectConversation, sendMessage
} from '../../services/messagesService';
import { lireRefusQuota, lireRefusFonction } from '../../utils/refusQuota';
import { construireContenuFiche, TYPE_MESSAGE_FICHE } from '../../utils/ficheRecommandee';

/**
 * Recommander la fiche d'un membre à un autre membre : un message de la messagerie
 * contenant une carte « Voir la fiche », avec un mot facultatif.
 *
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {number} userId - Celui qui recommande
 * @param {{id: number, peopleUuid: string, firstName: string, lastName: string}} target - La fiche recommandée
 * @param {boolean} autorise - Faux : fonction reservee aux abonnements payants, la fenetre ouvre l'offre
 * @param {Function} onNavigate - Pour la fenêtre « limite atteinte »
 */
const ShareProfileModal = ({ isOpen, onClose, userId, target, autorise = true, onNavigate }) => {
    const { t } = useTranslation();
    const l = t.profileShare || {};

    const [recherche, setRecherche] = useState('');
    const [resultats, setResultats] = useState([]);
    const [destinataire, setDestinataire] = useState(null);
    const [note, setNote] = useState('');
    const [erreur, setErreur] = useState('');
    const [envoi, setEnvoi] = useState(false);
    const [envoye, setEnvoye] = useState(false);
    const [limite, setLimite] = useState(null);
    const [reservee, setReservee] = useState(!autorise);
    const navigate = useNavigate();

    const nomFiche = `${target?.firstName || ''} ${target?.lastName || ''}`.trim();

    useEffect(() => {
        if (destinataire || recherche.trim().length < 2) {
            setResultats([]);
            return undefined;
        }
        let annule = false;
        const minuteur = setTimeout(async () => {
            const trouves = await searchParticipantsAndGroups(recherche.trim());
            if (annule) return;
            // Des membres seulement : ni groupes, ni soi-même, ni la fiche recommandée
            setResultats(trouves.filter(r =>
                r.type !== 'group' && r.id && r.id !== userId && r.id !== target?.id
            ));
        }, 300);
        return () => { annule = true; clearTimeout(minuteur); };
    }, [recherche, destinataire, userId, target?.id]);

    const fermer = () => {
        setEnvoye(false);
        setErreur('');
        onClose?.();
    };

    const envoyer = async () => {
        if (!destinataire) {
            setErreur(l.errorNoRecipient || '');
            return;
        }
        setEnvoi(true);
        setErreur('');
        try {
            const conv = await findOrCreateDirectConversation(destinataire.id);
            const refusConv = lireRefusQuota(conv);
            if (!conv.success) {
                if (refusConv) setLimite(refusConv);
                else setErreur(conv.message || l.errorGeneric || '');
                return;
            }
            const contenu = construireContenuFiche({ uuid: target.peopleUuid, nom: nomFiche, note });
            const res = await sendMessage(conv.conversationId, contenu, TYPE_MESSAGE_FICHE);
            const refus = lireRefusQuota(res);
            const codeRefus = String(res.denyCode ?? res.DENYCODE ?? '').toUpperCase();
            if (res.success) setEnvoye(true);
            else if (lireRefusFonction(res)) setReservee(true);
            else if (codeRefus === 'DESTINATAIRE_SANS_FONCTION') setErreur(l.errorRecipientNoFeature || '');
            else if (refus) setLimite(refus);
            else setErreur(res.message || l.errorGeneric || '');
        } finally {
            setEnvoi(false);
        }
    };

    const nomDestinataire = destinataire
        ? `${destinataire.firstName || ''} ${destinataire.lastName || ''}`.trim()
        : '';

    if (reservee) {
        return (
            <LimitReachedModal
                isOpen
                feature
                onClose={() => { setReservee(false); onClose?.(); }}
                moduleCode="CONTACTS"
                profileTypeId={parseInt(localStorage.getItem('userProfileTypeId'), 10)}
                peopleUuid={localStorage.getItem('userUuid')}
                onNavigate={(page, state) => { setReservee(false); onClose?.(); navigate('/', { state: { page, tabName: state?.tab } }); onNavigate?.(page, state); }}
            />
        );
    }

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={fermer}
                title={(l.title || 'Recommander {name}').replace('{name}', nomFiche)}
                icon={Send}
                size="md"
                onSubmit={envoye ? fermer : envoyer}
                submitText={envoye ? (l.close || 'Fermer') : (l.send || 'Envoyer')}
                loading={envoi}
            >
                {envoye ? (
                    <div className="text-center py-6 space-y-3" data-testid="fiche-envoyee">
                        <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                            <Check className="w-6 h-6 text-emerald-600" />
                        </div>
                        <p className="text-gray-800 font-medium">
                            {(l.sent || '').replace('{name}', nomFiche).replace('{to}', nomDestinataire)}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-5">
                        <p className="text-sm text-gray-600">{(l.intro || '').replace(/\{name\}/g, nomFiche)}</p>

                        {erreur && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm" role="alert">{erreur}</div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{l.recipientLabel} *</label>
                            {destinataire ? (
                                <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-purple-50 text-cabaza-purple">
                                    <span className="font-medium">{nomDestinataire}</span>
                                    <button
                                        type="button"
                                        onClick={() => { setDestinataire(null); setRecherche(''); }}
                                        aria-label={l.change}
                                        className="p-1 rounded hover:bg-purple-100"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="relative">
                                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                        <input
                                            type="text"
                                            value={recherche}
                                            onChange={(e) => setRecherche(e.target.value)}
                                            placeholder={l.searchPlaceholder}
                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                        />
                                    </div>
                                    {resultats.length > 0 && (
                                        <ul className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                                            {resultats.map(r => (
                                                <li key={`${r.type}-${r.id}`}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setDestinataire(r)}
                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                                    >
                                                        {`${r.firstName || ''} ${r.lastName || ''}`.trim()}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{l.noteLabel}</label>
                            <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                maxLength={500}
                                rows={3}
                                placeholder={l.notePlaceholder}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                            />
                        </div>
                    </div>
                )}
            </Modal>
            {limite && (
                <LimitReachedModal
                    isOpen
                    onClose={() => setLimite(null)}
                    moduleCode="MESSAGES"
                    current={limite.current}
                    max={limite.max}
                    periodUnit={limite.periodUnit}
                    periodCount={limite.periodCount}
                    profileTypeId={parseInt(localStorage.getItem('userProfileTypeId'), 10)}
                    peopleUuid={localStorage.getItem('userUuid')}
                    onNavigate={(page, state) => { setLimite(null); fermer(); onNavigate?.(page, state); }}
                />
            )}
        </>
    );
};

export default ShareProfileModal;
